/**
 * SRES · Project Setup & Resource Estimation — 20-step configuration.
 * (Sprint P37y · Sprint A — Feb 2026)
 *
 * The pipeline is reorganised into ONE professional sequence:
 *
 *   1  Project Details             2  Drillhole Data Upload
 *   3  Boundary & CRS Setup        4  Boundary GIS Maps
 *   5  Density & Cut-Off Setup     6  Data Validation
 *   7  Statistics & Grade Analysis 8  Composite Creation
 *   9  Domains & Wireframes       10  Variogram Modelling
 *  11  Estimation Parameters      12  Block Model
 *  13  Resource Estimation        14  Resource Classification
 *  15  Estimation Validation      16  CPR Configuration
 *  17  AI Technical Write-Ups     18  Final Review
 *  19  Report Generation & Downloads
 *  20  Project Data Bank
 *
 * Existing wizard screens are reused. Steps that don't yet have a
 * dedicated screen fall back to the closest existing component (marked
 * with `placeholder: true`) — they show the pipeline placeholder card
 * so the flow is complete but doesn't pretend to have new UI where
 * the backend is still unfinished (Sprint F will fill these in).
 */
import {
  IdentificationCard, Upload, MapTrifold, GlobeHemisphereWest,
  Ruler, CheckSquareOffset, ChartLineUp, Compass, Waves,
  Gear, Cube, ChartBar, Diamond, Scales, FileText, Sparkle, MagnifyingGlass,
  FilePdf, Archive, StackSimple,
} from "@phosphor-icons/react";

import ProjectSetupStep         from "@/pages/wizard/ProjectSetupStep";
import DataUploadStep           from "@/pages/wizard/DataUploadStep";
import DataValidationStep       from "@/pages/wizard/ValidationDashboardV2";
import StatisticsStep           from "@/pages/wizard/StatisticsStep";
import CompositeStep            from "@/pages/wizard/CompositeStep";import DomainStep               from "@/pages/wizard/DomainStep";
import VariogramStep            from "@/pages/wizard/VariogramStep";
import BlockModelWizardStep     from "@/pages/wizard/BlockModelWizardStep";
import ClassificationStep       from "@/pages/wizard/ClassificationStep";
import CPRGenerationStep        from "@/pages/wizard/CPRGenerationStep";
import GISStudioPanel           from "@/pages/wizard/GISStudioPanel";
import DensityMatrixEditor      from "@/pages/wizard/DensityMatrixEditor";
import DensityAndCutoffStep     from "@/pages/wizard/DensityAndCutoffStep";
import StepPlaceholder          from "@/pages/wizard/StepPlaceholder";
import EstimationSettingsStep   from "@/pages/wizard/EstimationSettingsStep";
import ModelValidationStep     from "@/pages/wizard/ModelValidationStep";
import FinalReviewStep          from "@/pages/wizard/steps/FinalReviewStep";
import AiReportGenerationStep   from "@/pages/wizard/steps/AiReportGenerationStep";

const _ok = () => ({ complete: true, blocking: false, message: "" });

/**
 * A checker helper that always renders as "In Progress" (not blocking)
 * for pipeline steps whose dedicated backend/frontend isn't shipped yet.
 * Sprint F will replace these one-by-one.
 */
const _in_progress = (message) => () => ({
  complete: false,
  blocking: false,
  message: message || "Coming in Sprint F — data available in Data Bank.",
});

export const WIZARD_STEPS = [
  // ── Foundation ─────────────────────────────────────────────────
  {
    id: "project_setup", n: 1, title: "Project Details",
    subtitle: "Identity · client · consultant · commodity · datum",
    icon: IdentificationCard, component: ProjectSetupStep,
    group: "Foundation",
    check: (p) => ({
      complete: !!(p?.name && p?.commodity && p?.country),
      blocking: false,
      message: p?.country ? "" : "Country is required",
    }),
  },
  {
    id: "data_upload", n: 2, title: "Drillhole Data Upload",
    subtitle: "Collar · survey · assay · lithology · density · QAQC",
    icon: Upload, component: DataUploadStep,
    group: "Foundation",
    check: (_, s) => {
      const collar = s?.collar?.rows || 0;
      const assay  = s?.assay?.rows || 0;
      return {
        complete: collar > 0 && assay > 0,
        blocking: !(collar > 0 && assay > 0),
        message: collar > 0 && assay > 0
          ? "" : "Collar and assay CSVs are required",
      };
    },
  },
  {
    id: "boundary_crs", n: 3, title: "Boundary & CRS Setup",
    subtitle: "KML/KMZ/SHP · EPSG · datum · axis-order",
    icon: MapTrifold, component: GISStudioPanel,
    group: "Foundation",
    check: () => _ok(),
  },
  {
    id: "gis_maps", n: 4, title: "Boundary GIS Maps",
    subtitle: "Location · geology · DEM · drainage · land-use",
    icon: GlobeHemisphereWest, component: GISStudioPanel,
    group: "Foundation",
    check: () => _ok(),
    external_route: (pid) => `/app/projects/${pid}/gis`,
  },
  // ── Data quality & analysis ────────────────────────────────────
  {
    id: "density_cutoff", n: 5, title: "Density & Cut-Off Setup",
    subtitle: "Bulk density · top-cut · reporting cutoff · economics",
    icon: Ruler, component: DensityAndCutoffStep,
    group: "Data & Analysis",
    check: () => _ok(),
  },
  {
    id: "data_validation", n: 6, title: "Data Validation",
    subtitle: "Cross-file checks · auto-fix · manual review",
    icon: CheckSquareOffset, component: DataValidationStep,
    group: "Data & Analysis",
    check: (_, s) => {
      const nErrors = s?.__validation?.n_errors ?? 0;
      return {
        complete: (s?.collar?.rows || 0) > 0 && nErrors === 0,
        blocking: nErrors > 0,
        message: nErrors > 0 ? `${nErrors} critical errors — auto-fix or resolve manually` : "",
      };
    },
  },
  {
    id: "statistics", n: 7, title: "Statistics & Grade Analysis",
    subtitle: "Histograms · Q-Q · outliers · declustering · GT",
    icon: ChartLineUp, component: StatisticsStep,
    group: "Data & Analysis",
    check: (_, s) => ({
      complete: (s?.assay?.rows || 0) > 0,
      blocking: (s?.assay?.rows || 0) === 0,
      message: (s?.assay?.rows || 0) > 0 ? "" : "Requires assay data",
    }),
  },
  {
    id: "composite", n: 8, title: "Composite Creation",
    subtitle: "Length-weighted · residual · domain-honoured",
    icon: StackSimple, component: CompositeStep,
    group: "Data & Analysis",
    check: () => _ok(),
  },
  // ── Modelling ─────────────────────────────────────────────────
  {
    id: "domain", n: 9, title: "Domains & Wireframes",
    subtitle: "Grade shells · lithology · implicit · explicit",
    icon: Compass, component: DomainStep,
    group: "Modelling",
    check: () => _ok(),
  },
  {
    id: "variogram", n: 10, title: "Variogram Modelling",
    subtitle: "Experimental · directional · downhole · fit",
    icon: Waves, component: VariogramStep,
    group: "Modelling",
    check: () => _ok(),
  },
  {
    id: "estimation_params", n: 11, title: "Estimation Parameters",
    subtitle: "Blocks · search · min/max samples · discretisation",
    icon: Gear, component: EstimationSettingsStep,
    group: "Modelling",
    check: () => _ok(),
  },
  {
    id: "block_model", n: 12, title: "Block Model",
    subtitle: "Grid · OK / IDW / NN · async with live progress",
    icon: Cube, component: BlockModelWizardStep,
    group: "Modelling",
    check: (_, s) => ({
      complete: !!s?.__has_bm,
      blocking: false,
      message: s?.__has_bm ? "" : "Block model not yet built",
    }),
  },
  {
    id: "resource_estimation", n: 13, title: "Resource Estimation",
    subtitle: "Grade × tonnage × recovery per domain",
    icon: ChartBar, component: BlockModelWizardStep,
    group: "Estimation",
    check: (_, s) => ({
      complete: !!s?.__has_bm, blocking: false,
      message: s?.__has_bm ? "" : "Run estimation from the block-model step",
    }),
  },
  {
    id: "classification", n: 14, title: "Resource Classification",
    subtitle: "Measured · Indicated · Inferred (JORC/NI 43-101)",
    icon: Diamond, component: ClassificationStep,
    group: "Estimation",
    check: (_, s) => ({
      complete: !!s?.__has_bm, blocking: false,
      message: s?.__has_bm ? "" : "Requires block model",
    }),
  },
  {
    id: "estimation_validation", n: 15, title: "Estimation Validation",
    subtitle: "Swath plots · slope of regression · bias · smoothing ratio",
    icon: Scales, component: ModelValidationStep,
    group: "Estimation",
    check: (_, s) => ({
      complete: !!s?.__has_bm, blocking: false,
      message: s?.__has_bm ? "" : "Requires block model",
    }),
  },
  // ── Report & Delivery ─────────────────────────────────────────
  {
    id: "cpr_config", n: 16, title: "Report Configuration",
    subtitle: "Report type · standard · CP · appendices (CPR · MRE · Exploration · Feasibility · Technical)",
    icon: FileText, component: CPRGenerationStep,
    group: "Reporting",
    check: (_, s) => ({
      complete: !!s?.__has_bm, blocking: false,
      message: s?.__has_bm ? "" : "Complete estimation first",
    }),
  },
  {
    id: "ai_narratives", n: 17, title: "AI Technical Write-Ups",
    subtitle: "27-chapter narrative · evidence-linked · review",
    icon: Sparkle, component: AiReportGenerationStep,
    group: "Reporting",
    check: _ok,
  },
  {
    id: "final_review", n: 18, title: "Final Review",
    subtitle: "CP/QP sign-off · paragraph statuses · signatures",
    icon: MagnifyingGlass, component: FinalReviewStep,
    group: "Reporting",
    check: _ok,
  },
  {
    id: "report_generation", n: 19, title: "Generate Report",
    subtitle: "CPR · Technical · Geological · Resource · QA/QC · Maps · Logs · Custom reports",
    icon: FilePdf, component: CPRGenerationStep,
    group: "Reporting",
    check: (_, s) => ({
      complete: !!s?.__has_bm, blocking: false,
      message: s?.__has_bm ? "" : "Complete estimation first",
    }),
    external_route: (pid) => `/app/projects/${pid}/wizard/cpr_config`,
  },
  {
    id: "data_bank", n: 20, title: "Reports Panel",
    subtitle: "Preview · download · search · filter · version history",
    icon: Archive, component: StepPlaceholder,
    group: "Reporting",
    external_route: (pid) => `/app/projects/${pid}/reports`,
    check: () => _ok(),
  },
];


// Group summaries for the ordered pipeline card and progress dashboard
// (labels match Feb 2026 client mandate — five main workflow sections)
export const WIZARD_GROUPS = [
  { key: "Foundation",       label: "Foundation",                hue: "brand-blue" },
  { key: "Data & Analysis", label: "Data Quality & Analysis",   hue: "sky" },
  { key: "Modelling",        label: "Geological Modelling",       hue: "violet" },
  { key: "Estimation",       label: "Resource Estimation",        hue: "emerald" },
  { key: "Reporting",        label: "Reporting & Delivery",       hue: "amber" },
];
